import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, finalize, map, Observable } from 'rxjs';
import { UserService } from '@reg/shared/service/user.service';
import { PlanSubscription } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get plans from core and to purchase a FloPro subscription
 */
@Injectable()
export class PlanSubscriptionService {
    /**
     * Constructor
     */
    constructor(
                private http: HttpServiceHelper,
                private userService: UserService,
    ) {
    }

    public getPlans(): Observable<PlanSubscription[]> {
        const siteId: string = this.http.configService.getSportConfig('siteId');
        const baseUrl: string = this.http.configService.getBaseConfig('flosportsApiBaseUrl');
        const apiRequestUrl: string = `${baseUrl}/api/plans?site_id=${siteId}`;

        return this.http.get(apiRequestUrl)
            .pipe(
                map((response: any) => {
                    const json: any = response.body.body;

                    const plans: PlanSubscription[] = [];
                    for (const plan of json.data) {
                        plans.push((new PlanSubscription()).deserialize(plan));
                    }

                    return plans;
                }),
                finalize(() => {
                    this.userService.clearCache();
                }),
            );
    }

    /**
     * Get the stripe public key for signing up for FloPro
     */
    public getFloProStripePublicKeyBySiteId(siteId: number): Observable<string> {
        const apiBaseUrl: string = this.http.configService.getBaseConfig('flosportsApiBaseUrl');
        const apiRequestUrl: string = `${apiBaseUrl}/api/plans?site_id=${siteId}`;

        // Make the http request
        return this.http.get(apiRequestUrl, false, true, this.http.getHeadersForJson(true))
            .pipe(map((response: IJsonApiResponse) => {
                const bodyData: any = response.data || [];
                if (bodyData &&
                    bodyData.length > 0 &&
                    bodyData[0].payment_provider?.public_key) {
                    return bodyData[0].payment_provider.public_key;
                }

                return null;
            }));
    }

    public purchaseFloProSubscription(
        userId: number,
        planId: number,
        stripeTokenId: string,
        refCode: string,
    ): Observable<boolean> {
        const apiRequestUrl: string =
            `${this.http.configService.getBaseConfig('flosportsApiBaseUrl')}/api/users/${userId}/stripe-subscriptions`;
        const requestBody: any = {};
        requestBody.plan = planId;
        requestBody.token = stripeTokenId;
        requestBody.user = userId;
        requestBody.ms_ref_code = refCode;
        requestBody.ms_sub_site_id = this.http.configService.getSportConfig('siteId');

        return this.http.post(apiRequestUrl, JSON.stringify(requestBody), true, false)
            .pipe(
                map(() => true),
                finalize(() => {
                    this.userService.clearCache();
                }),
                catchError((error: HttpErrorResponse) => {
                    this.http.toastService.showError('An error occurred trying to add FloPRO', null, 5000);
                    return this.http.handleError(error, null);
                }),
            );
    }
}
